// Tenantra Inc. - Owner-only admin staff management (invite by email, per-section access).
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Trash2, Mail, ShieldCheck } from 'lucide-react';
import api, { apiHelpers } from '@/services/api';

const LABELS = {
  analytics: 'Analytics', landlords: 'Landlords', properties: 'Properties', ownership_review: 'Ownership Review',
  tenants: 'Tenants', reports: 'Reports', tenancies: 'Tenancies', consent_requests: 'Consent Requests',
  subscriptions: 'Subscriptions', discounts: 'Discounts & Promos', payments: 'Payments', equifax: 'Equifax Reporting',
  payment_fees: 'Payment Fees', fee_revenue: 'Fee Revenue', wallets: 'Rent Wallets', tickets: 'Tickets',
  disputes: 'Disputes', careers: 'Careers', users: 'Users',
};

function SectionGrid({ selected, onToggle, idPrefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {Object.keys(LABELS).map((key) => (
        <label key={key} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`${idPrefix}-section-${key}`}>
          <input type="checkbox" checked={selected.includes(key)} onChange={() => onToggle(key)} className="rounded border-slate-300 text-teal-600 focus:ring-teal-500" />
          {LABELS[key]}
        </label>
      ))}
    </div>
  );
}

function StaffRow({ s, onChanged }) {
  const [perms, setPerms] = useState(s.admin_permissions || []);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify([...perms].sort()) !== JSON.stringify([...(s.admin_permissions || [])].sort());
  const toggle = (k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const act = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setBusy(false); } };

  if (s.is_owner) {
    return (
      <div className="border border-slate-200 rounded-lg p-4 bg-slate-50" data-testid={`staff-row-${s.id}`}>
        <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-teal-600" /><span className="font-medium text-slate-800">{s.email}</span><span className="text-[11px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-700">Owner</span></div>
        <p className="text-xs text-slate-500 mt-1">Full access to every section. Cannot be changed or removed.</p>
      </div>
    );
  }
  return (
    <div className="border border-slate-200 rounded-lg p-4" data-testid={`staff-row-${s.id}`}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <span className="font-medium text-slate-800">{s.email}</span>
          <span className={`ml-2 text-[11px] px-2 py-0.5 rounded-full ${s.status === 'active' ? 'bg-emerald-100 text-emerald-700' : s.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-600'}`}>{s.status}</span>
        </div>
        <div className="flex items-center gap-2">
          {s.status === 'pending' && <button disabled={busy} onClick={() => act(() => api.post(`/admin/staff/${s.id}/resend`), 'Invite re-sent')} className="h-8 px-2.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`staff-resend-${s.id}`}><Mail className="w-3.5 h-3.5" />Resend</button>}
          {s.status !== 'pending' && <button disabled={busy} onClick={() => act(() => api.patch(`/admin/staff/${s.id}`, { status: s.status === 'active' ? 'disabled' : 'active' }), 'Updated')} className="h-8 px-2.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50" data-testid={`staff-toggle-status-${s.id}`}>{s.status === 'active' ? 'Deactivate' : 'Activate'}</button>}
          <button disabled={busy} onClick={() => { if (window.confirm(`Remove ${s.email}?`)) act(() => api.delete(`/admin/staff/${s.id}`), 'Removed'); }} className="h-8 px-2.5 rounded-md border border-red-200 text-xs text-red-600 hover:bg-red-50 inline-flex items-center gap-1" data-testid={`staff-remove-${s.id}`}><Trash2 className="w-3.5 h-3.5" />Remove</button>
        </div>
      </div>
      <SectionGrid selected={perms} onToggle={toggle} idPrefix={`staff-${s.id}`} />
      {dirty && <button disabled={busy} onClick={() => act(() => api.patch(`/admin/staff/${s.id}`, { permissions: perms }), 'Access updated')} className="mt-3 h-8 px-4 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium inline-flex items-center gap-2" data-testid={`staff-save-${s.id}`}>{busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}Save access</button>}
    </div>
  );
}

export default function AdminStaffPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [perms, setPerms] = useState([]);
  const [inviting, setInviting] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/staff'); setItems(r.data.items || []); }
    catch (e) { if (e?.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!email.trim()) { toast.error('Enter an email'); return; }
    setInviting(true);
    try { await api.post('/admin/staff', { email: email.trim(), permissions: perms }); toast.success('Invitation sent ✓'); setEmail(''); setPerms([]); load(); }
    catch (e) { toast.error(apiHelpers.handleError(e)); } finally { setInviting(false); }
  };

  if (denied) return <div className="p-6"><p className="text-slate-600">Only the owner admin can manage staff.</p></div>;

  return (
    <div className="space-y-6" data-testid="admin-staff-panel">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Admin team</h1>
        <p className="text-sm text-slate-500">Invite staff by email and choose exactly which sections they can access. Turn access on or off anytime.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Invite a new admin</h2>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@example.com" autoComplete="off" className="w-full h-10 px-3 mb-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none" data-testid="staff-invite-email" />
        <p className="text-xs font-medium text-slate-600 mb-2">Sections they can access</p>
        <SectionGrid selected={perms} onToggle={(k) => setPerms((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]))} idPrefix="invite" />
        <button disabled={inviting} onClick={invite} className="mt-4 h-10 px-5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium inline-flex items-center gap-2" data-testid="staff-invite-submit">{inviting && <Loader2 className="w-4 h-4 animate-spin" />}Send email invite</button>
      </div>

      <div className="space-y-3">
        {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          : items.map((s) => <StaffRow key={s.id} s={s} onChanged={load} />)}
      </div>
    </div>
  );
}
